import { TIMESHEET_CONSTANTS } from '@pages/vendor-rep/timesheet/timesheet.constants';
import { ErrorMessages } from '@constants/constants';

export interface TimesheetDataStatus {
  status: string | null;
  workingHrs: number;
  billableHrs: number;
  employeeId?: string;
  employeeName?: string;
}

export interface TimesheetValidationResult {
  hasStatus: boolean;
  hasWorkingHours: boolean;
  hasBillableHours: boolean;
  isActionable: boolean;
  isPendingSubmission: boolean;
}

export interface DataLoadedStatusResult {
  isDataLoaded: boolean;
  errorMsg: string;
}

export type ComponentType = 'vendor-rep' | 'vendor-management';

/**
 * Validates timesheet row data for checkbox display and selection
 */
export function validateTimesheetRow<T extends TimesheetDataStatus>(row: T): TimesheetValidationResult {
  if (!row) {
    return {
      hasStatus: false,
      hasWorkingHours: false,
      hasBillableHours: false,
      isActionable: false,
      isPendingSubmission: true
    };
  }

  const hasStatus = row.status !== null && row.status !== undefined;
  const hasWorkingHours = (row.workingHrs || 0) > 0;
  const hasBillableHours = (row.billableHrs || 0) > 0;
  const isActionable = hasStatus || hasWorkingHours || hasBillableHours;
  const isPendingSubmission = !hasStatus && !hasWorkingHours && !hasBillableHours;

  return {
    hasStatus,
    hasWorkingHours,
    hasBillableHours,
    isActionable,
    isPendingSubmission
  };
}

/**
 * Determines if checkbox should be shown for timesheet row
 */
export function shouldShowTimesheetCheckbox<T extends TimesheetDataStatus>(row: T, componentType: ComponentType): boolean {
  const validation = validateTimesheetRow(row);
  return validation.isActionable;
}

/**
 * Checks if there are pending items based on component type
 */
export function hasPendingItems<T extends TimesheetDataStatus>(data: T[], componentType: ComponentType): boolean {
  if (componentType === 'vendor-rep') {
    return data.some(row => row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION);
  }
  return data.some(row => row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL);
}

/**
 * Updates data loaded status with appropriate error message
 */
export function getDataLoadedStatus<T extends TimesheetDataStatus>(data: T[], componentType: ComponentType): DataLoadedStatusResult {
  if (data.length === 0) {
    return {
      isDataLoaded: false,
      errorMsg: ErrorMessages.NoDataAvailable
    };
  }

  const hasPending = hasPendingItems(data, componentType);
  if (!hasPending) {
    const errorMsg = componentType === 'vendor-rep' 
      ? TIMESHEET_CONSTANTS.MESSAGES.NO_PENDING_SUBMISSIONS
      : TIMESHEET_CONSTANTS.MESSAGES.NO_PENDING_APPROVALS;
    
    return {
      isDataLoaded: false,
      errorMsg
    };
  }

  return {
    isDataLoaded: true,
    errorMsg: ''
  };
}

/**
 * Determines if timesheet row is empty (pending submission)
 */
export function isEmptyTimesheet<T extends TimesheetDataStatus>(row: T): boolean {
  return [
    row.status === null,
    row.status === undefined,
    row.status === TIMESHEET_CONSTANTS.STATUS.PENDING_SUBMISSION,
  ].some(condition => condition) && [
    Number(row.workingHrs || 0) === 0,
    Number(row.billableHrs || 0) === 0
  ].every(condition => condition);
}

/**
 * Gets CSS class for status icons
 */
export function getStatusIconClass(status: string): string {
  switch (status) {
    case TIMESHEET_CONSTANTS.STATUS.APPROVED:
      return TIMESHEET_CONSTANTS.CSS_CLASSES.GREEN_TEXT;
    case TIMESHEET_CONSTANTS.STATUS.REJECTED:
      return TIMESHEET_CONSTANTS.CSS_CLASSES.RED_TEXT;
    default:
      return TIMESHEET_CONSTANTS.CSS_CLASSES.BLACK_TEXT;
  }
}

/**
 * Gets hours suffix from constants
 */
export function getHoursSuffix(): string {
  return TIMESHEET_CONSTANTS.LABELS.HOURS_SUFFIX;
}

/**
 * Filters actionable rows for selection
 */
export function filterActionableRows<T extends TimesheetDataStatus>(rows: T[]): T[] {
  return rows.filter(row => {
    const validation = validateTimesheetRow(row);
    return validation.isActionable && !validation.isPendingSubmission;
  });
}
